import { useState } from "react";
import { apiClient } from "@/api/base44Client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, Shield, ExternalLink } from "lucide-react";
import { PERMISSIONS } from "@/lib/dataroom";

const PARTY_LABELS = { kjoper: "Kjøper", selger: "Selger" };

export default function AccessTab({ deal, canManage }) {
  const qc = useQueryClient();
  const dealId = deal.id;
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [permission, setPermission] = useState("view");
  const [party, setParty] = useState("none");
  const [external, setExternal] = useState(true);
  const [removeTarget, setRemoveTarget] = useState(null);

  const addMutation = useMutation({
    mutationFn: (payload) => apiClient.post(`/deals/${dealId}/members`, payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      qc.invalidateQueries({ queryKey: ["deal-activity", dealId] });
      setEmail(""); setName(""); setParty("none");
      const emailed = res?.data?.email_configured;
      toast.success(emailed ? "Tilgang gitt – invitasjon sendt på e-post" : "Tilgang gitt");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke gi tilgang"),
  });

  const removeMutation = useMutation({
    mutationFn: (memberEmail) => apiClient.delete(`/deals/${dealId}/members/${encodeURIComponent(memberEmail)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      setRemoveTarget(null);
      toast.success("Tilgang fjernet");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Kunne ikke fjerne"),
  });

  const members = deal.members || [];

  return (
    <div className="space-y-6">
      {canManage && (
        <div className="bg-white rounded-2xl border border-slate-200/70 p-5">
          <h4 className="font-semibold text-brand-ink mb-4 flex items-center gap-2"><UserPlus className="w-4 h-4" /> Inviter part</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>E-post</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="part@radgiver.no" className="mt-1.5" data-testid="member-email-input" />
            </div>
            <div>
              <Label>Navn</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Navn (valgfritt)" className="mt-1.5" data-testid="member-name-input" />
            </div>
            <div>
              <Label>Tilgangsnivå</Label>
              <Select value={permission} onValueChange={setPermission}>
                <SelectTrigger className="mt-1.5" data-testid="member-permission-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PERMISSIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Part</Label>
              <Select value={party} onValueChange={setParty}>
                <SelectTrigger className="mt-1.5" data-testid="member-party-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ingen / ikke angitt</SelectItem>
                  <SelectItem value="kjoper">Kjøper</SelectItem>
                  <SelectItem value="selger">Selger</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 px-4 mt-1.5">
              <div>
                <p className="text-sm font-medium text-brand-ink">Ekstern part</p>
                <p className="text-xs text-slate-400">Ser kun dette datarommet</p>
              </div>
              <Switch checked={external} onCheckedChange={setExternal} data-testid="member-external-switch" />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-400">Eksterne parter ser kun dette datarommet. Alle dokumenter vannmerkes med nedlasterens navn og tidspunkt ved nedlasting og forhåndsvisning.</p>
            <Button disabled={!email.trim() || addMutation.isPending} onClick={() => addMutation.mutate({ email: email.trim(), name: name.trim(), permission, external, party: party === "none" ? null : party })} data-testid="add-member-button">
              {addMutation.isPending ? "Legger til..." : "Gi tilgang"}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200/70 overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 text-sm font-semibold text-brand-ink">Parter med tilgang ({members.length})</div>
        <div className="divide-y divide-slate-100">
          {members.map((m) => (
            <div key={m.email} data-testid={`member-${m.email}`} className="flex items-center gap-3 px-5 py-3">
              <span className="w-9 h-9 rounded-full bg-brand-ink text-brand-lime flex items-center justify-center text-xs font-bold shrink-0">
                {(m.name || m.email).split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-brand-ink text-sm truncate flex items-center gap-2">
                  {m.name || m.email}
                  {m.party && <Badge className="border-0 bg-brand-ink text-brand-lime text-[10px]" data-testid={`member-party-${m.email}`}>{PARTY_LABELS[m.party]}</Badge>}
                  {m.external && <Badge className="border-0 bg-slate-100 text-slate-600 text-[10px] gap-1"><ExternalLink className="w-3 h-3" /> Ekstern</Badge>}
                </p>
                <p className="text-xs text-slate-400 truncate">{m.email}</p>
              </div>
              <Badge className="border-0 bg-brand-limeSoft text-brand-limeDark gap-1">
                {m.permission === "manage" && <Shield className="w-3 h-3" />}{PERMISSIONS[m.permission]}
              </Badge>
              {canManage && (
                <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-400 hover:text-red-600" onClick={() => setRemoveTarget(m)} data-testid={`remove-member-${m.email}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fjerne tilgang?</AlertDialogTitle>
            <AlertDialogDescription>{removeTarget?.name || removeTarget?.email} mister tilgang til datarommet.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => removeMutation.mutate(removeTarget.email)}>Fjern</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
